import { useState, useRef } from "react";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { ReportPreview } from "../components/ReportPreview";
import { extractPdf, runAppraisal } from "../lib/api";
import { US_STATES } from "../lib/states";
import { SCENARIOS } from "../lib/scenarios";
import {
  UploadSimple, Sparkle, Spinner, Car, Wrench, FileArrowUp, X,
  ShieldCheck, Warning, Lightning,
} from "@phosphor-icons/react";

const emptyForm = {
  lead_name: "", lead_email: "",
  year: "", make: "", model: "", trim: "", vin: "", mileage: "",
  vehicle_type: "Gas", location: "", state: "", condition: "Good",
  damage_severity: "Moderate", repair_cost: "", structural_damage: false,
  airbag_deployed: false, damage_description: "",
  owner_name: "", claim_number: "", insurance_company: "",
};

const labelCls = "block text-[11px] uppercase tracking-wider font-semibold text-gray-400 mb-1";
const inputCls =
  "w-full px-3 py-2 text-sm bg-[#141417] border border-white/10 rounded-md focus:ring-1 focus:ring-[#C5A059] focus:border-[#C5A059] text-white placeholder:text-gray-600 outline-none transition-colors";
const selectCls = inputCls + " appearance-none cursor-pointer";

const Field = ({ label, children }) => (
  <div>
    <span className={labelCls}>{label}</span>
    {children}
  </div>
);

const SectionHead = ({ icon: Icon, num, title, note }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <div className="p-1.5 bg-[#C5A059]/10 rounded-sm text-[#C5A059] border border-[#C5A059]/20">
        <Icon size={16} weight="bold" />
      </div>
      <h2 className="text-base font-display font-bold text-white tracking-wide">
        {num}. {title}
      </h2>
    </div>
    {note && <span className="text-[11px] uppercase tracking-wider font-medium text-[#C5A059]">{note}</span>}
  </div>
);

export default function Appraiser() {
  const [form, setForm] = useState(emptyForm);
  const [appraisal, setAppraisal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file.");
      return;
    }
    setFileName(file.name);
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await extractPdf(fd);
      const d = res.data || {};
      setForm((f) => ({
        ...f,
        year: d.year ?? f.year,
        make: d.make ?? f.make,
        model: d.model ?? f.model,
        trim: d.trim ?? f.trim,
        vin: d.vin ?? f.vin,
        mileage: d.mileage ?? f.mileage,
        vehicle_type: d.vehicle_type ?? f.vehicle_type,
        location: d.location ?? f.location,
        repair_cost: d.repair_cost ?? f.repair_cost,
        claim_number: d.claim_number ?? f.claim_number,
        insurance_company: d.insurance_company ?? f.insurance_company,
        owner_name: d.owner_name ?? f.owner_name,
        structural_damage: d.structural_damage ?? f.structural_damage,
        airbag_deployed: d.airbag_deployed ?? f.airbag_deployed,
        damage_description: d.damage_description ?? f.damage_description,
      }));
      toast.success("PDF parsed — fields auto-filled. Review & run appraisal.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read PDF. Enter details manually.");
      setFileName("");
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setFileName("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const buildPayload = (f) => ({
    lead_name: f.lead_name,
    lead_email: f.lead_email,
    vehicle: {
      year: f.year ? parseInt(f.year) : null,
      make: f.make,
      model: f.model,
      trim: f.trim || null,
      vin: f.vin || null,
      mileage: f.mileage ? parseInt(f.mileage) : 0,
      vehicle_type: f.vehicle_type,
      location: f.location || null,
      state: f.state || null,
      condition: f.condition,
    },
    accident: {
      damage_severity: f.damage_severity,
      repair_cost: f.repair_cost ? parseFloat(f.repair_cost) : 0,
      structural_damage: f.structural_damage,
      airbag_deployed: f.airbag_deployed,
      damage_description: f.damage_description || null,
    },
    owner_name: f.owner_name || null,
    claim_number: f.claim_number || null,
    insurance_company: f.insurance_company || null,
  });

  const runWith = async (f) => {
    setLoading(true);
    setAppraisal(null);
    try {
      const res = await runAppraisal(buildPayload(f));
      setAppraisal(res);
      toast.success("Appraisal complete.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Appraisal failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submit = () => {
    if (!form.make || !form.model || !form.year) {
      toast.error("Please provide at least the vehicle year, make and model.");
      return;
    }
    runWith(form);
  };

  const handleScenario = (sc) => {
    const merged = { ...emptyForm, ...sc, lead_name: form.lead_name, lead_email: form.lead_email };
    setForm(merged);
    setFileName("");
    if (fileRef.current) fileRef.current.value = "";
    runWith(merged);
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const trigger = (
    <label
      htmlFor="switch-structural"
      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
        form.structural_damage ? "bg-rose-950/20 border-rose-500/50" : "bg-[#141417] border-white/10 hover:bg-[#1A1A1F]"
      }`}
    >
      <input
        id="switch-structural"
        data-testid="structural-switch"
        type="checkbox"
        checked={form.structural_damage}
        onChange={(e) => set("structural_damage", e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-rose-500"
      />
      <div className="space-y-0.5">
        <span className={`text-xs font-bold block ${form.structural_damage ? "text-rose-300" : "text-white"}`}>
          Structural / Frame Damage
        </span>
        <p className="text-[11px] text-gray-400 leading-tight">Frame or pillar repair — flagged as structural on Carfax.</p>
      </div>
    </label>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-gray-200 flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
        {/* Intro banner */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#0F0F11] p-4 rounded-xl border border-white/10 shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#C5A059]/10 text-[#C5A059] rounded-sm border border-[#C5A059]/20">
              <ShieldCheck className="w-5 h-5" weight="fill" />
            </div>
            <div>
              <h2 className="text-sm font-display font-bold text-white">
                Diminished Value Compensation Engine
              </h2>
              <p className="text-xs text-gray-400">
                Retail-market loss appraisal — evidence built to get insurers to pay.
              </p>
            </div>
          </div>
          <span className="px-2.5 py-1 bg-[#10B981]/10 text-[#10B981] font-semibold rounded-sm border border-[#10B981]/20 text-xs">
            Appraiser-Grade Report
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left: form */}
          <div className="lg:col-span-6 xl:col-span-5 space-y-6">
            {/* Quick demo scenarios */}
            <div className="bg-[#0F0F11] rounded-xl border border-white/10 shadow-2xl p-4 sm:p-5" data-testid="scenarios-card">
              <div className="flex items-center gap-2 mb-3">
                <Lightning size={15} weight="fill" className="text-[#C5A059]" />
                <span className="text-[11px] font-bold text-[#E5C17B] uppercase tracking-widest">
                  Quick Demo Scenarios
                </span>
                <span className="text-[11px] text-gray-500 hidden sm:inline ml-auto">One tap → instant dossier</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {SCENARIOS.map((sc) => (
                  <button
                    key={sc.id}
                    type="button"
                    data-testid={`scenario-btn-${sc.id}`}
                    onClick={() => handleScenario(sc)}
                    disabled={loading}
                    className="group text-left px-3 py-1.5 bg-[#141417] hover:bg-[#1A1A1F] border border-white/10 hover:border-[#C5A059]/40 rounded-sm transition-all text-xs text-gray-300 hover:text-white flex items-center gap-2 disabled:opacity-50"
                  >
                    <span className="font-semibold group-hover:text-[#E5C17B]">
                      {sc.year} {sc.make} {sc.model}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-black/60 text-gray-400 group-hover:text-[#E5C17B] rounded-sm font-mono-num">
                      {sc.badge}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* PDF upload */}
            <div className="bg-[#0F0F11] rounded-xl border border-white/10 shadow-2xl p-5 sm:p-6 space-y-3">
              <SectionHead icon={FileArrowUp} num="1" title="Insurance Estimate" note="AI Scan" />
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                onChange={handleFile}
                className="hidden"
                data-testid="pdf-file-input"
              />
              {!fileName ? (
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="pdf-upload-zone"
                  className="w-full border border-dashed border-[#C5A059]/30 bg-gradient-to-r from-[#C5A059]/10 via-black/40 to-black/60 hover:border-[#C5A059]/60 transition-colors p-6 text-center rounded-lg"
                >
                  <UploadSimple size={26} className="mx-auto text-[#C5A059] mb-2" />
                  <div className="text-sm font-semibold text-white">Upload PDF estimate</div>
                  <div className="text-xs text-gray-400 mt-1">
                    AI auto-fills vehicle, mileage, location &amp; repair costs
                  </div>
                </button>
              ) : (
                <div className="w-full border border-[#C5A059]/30 bg-black/40 p-3 rounded-lg flex items-center gap-3">
                  {uploading ? (
                    <Spinner size={20} className="animate-spin text-[#C5A059] shrink-0" />
                  ) : (
                    <Sparkle size={20} weight="fill" className="text-[#C5A059] shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{fileName}</div>
                    <div className="text-xs text-gray-400">
                      {uploading ? "Extracting data…" : "Fields auto-filled below"}
                    </div>
                  </div>
                  {!uploading && (
                    <button onClick={clearFile} data-testid="clear-pdf-btn" className="text-gray-400 hover:text-white">
                      <X size={18} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Vehicle */}
            <div className="bg-[#0F0F11] rounded-xl border border-white/10 shadow-2xl p-5 sm:p-6 space-y-4">
              <SectionHead icon={Car} num="2" title="Vehicle Specifications" note="Pre-Accident Baseline" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Year">
                  <input data-testid="year-input" type="number" value={form.year}
                    onChange={(e) => set("year", e.target.value)} placeholder="2022" className={inputCls} />
                </Field>
                <Field label="Make">
                  <input data-testid="make-input" value={form.make}
                    onChange={(e) => set("make", e.target.value)} placeholder="Tesla" className={inputCls} />
                </Field>
                <Field label="Model">
                  <input data-testid="model-input" value={form.model}
                    onChange={(e) => set("model", e.target.value)} placeholder="Model 3" className={inputCls} />
                </Field>
                <Field label="Trim">
                  <input data-testid="trim-input" value={form.trim}
                    onChange={(e) => set("trim", e.target.value)} placeholder="LR" className={inputCls} />
                </Field>
              </div>
              <Field label="VIN (optional)">
                <input data-testid="vin-input" value={form.vin} maxLength={17}
                  onChange={(e) => set("vin", e.target.value.toUpperCase())} placeholder="17-character VIN"
                  className={inputCls + " uppercase font-mono-num"} />
              </Field>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Field label="Mileage">
                  <input data-testid="mileage-input" type="number" value={form.mileage}
                    onChange={(e) => set("mileage", e.target.value)} placeholder="24000" className={inputCls + " font-mono-num"} />
                </Field>
                <Field label="Vehicle Type">
                  <select data-testid="vehicle-type-select" value={form.vehicle_type}
                    onChange={(e) => set("vehicle_type", e.target.value)} className={selectCls}>
                    {["Gas", "Electric", "Hybrid", "Diesel"].map((o) => (
                      <option key={o} value={o} className="bg-[#141417]">{o}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Condition">
                  <select data-testid="condition-select" value={form.condition}
                    onChange={(e) => set("condition", e.target.value)} className={selectCls}>
                    {["Excellent", "Good", "Fair", "Poor"].map((o) => (
                      <option key={o} value={o} className="bg-[#141417]">{o}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Location / City">
                  <input data-testid="location-input" value={form.location}
                    onChange={(e) => set("location", e.target.value)} placeholder="Austin, TX" className={inputCls} />
                </Field>
                <Field label="State (DV rules & deadlines)">
                  <select data-testid="state-select" value={form.state}
                    onChange={(e) => set("state", e.target.value)} className={selectCls}>
                    <option value="" className="bg-[#141417]">Select state</option>
                    {US_STATES.map((s) => (
                      <option key={s.code} value={s.code} className="bg-[#141417]">{s.name}</option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>

            {/* Accident */}
            <div className="bg-[#0F0F11] rounded-xl border border-white/10 shadow-2xl p-5 sm:p-6 space-y-4">
              <SectionHead icon={Wrench} num="3" title="Repair Costs & Damage" note="From Body Shop Invoice" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Damage Severity">
                  <select data-testid="severity-select" value={form.damage_severity}
                    onChange={(e) => set("damage_severity", e.target.value)} className={selectCls}>
                    {["Minor", "Moderate", "Major", "Severe"].map((o) => (
                      <option key={o} value={o} className="bg-[#141417]">{o}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Total Repair Cost ($)">
                  <input data-testid="repair-cost-input" type="number" value={form.repair_cost}
                    onChange={(e) => set("repair_cost", e.target.value)} placeholder="8500" className={inputCls + " font-mono-num"} />
                </Field>
              </div>
              <div className="text-[11px] font-bold text-gray-300 uppercase tracking-widest flex items-center gap-1.5 pt-1">
                <Warning className="w-3.5 h-3.5 text-[#C5A059]" weight="fill" />
                <span>High-Stigma Vehicle History Triggers</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {trigger}
                <label
                  htmlFor="switch-airbags"
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    form.airbag_deployed ? "bg-amber-950/20 border-amber-500/50" : "bg-[#141417] border-white/10 hover:bg-[#1A1A1F]"
                  }`}
                >
                  <input
                    id="switch-airbags"
                    data-testid="airbag-switch"
                    type="checkbox"
                    checked={form.airbag_deployed}
                    onChange={(e) => set("airbag_deployed", e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-amber-500"
                  />
                  <div className="space-y-0.5">
                    <span className={`text-xs font-bold block ${form.airbag_deployed ? "text-amber-300" : "text-white"}`}>
                      Airbag(s) Deployed
                    </span>
                    <p className="text-[11px] text-gray-400 leading-tight">SRS replaced — triggers critical safety stigma.</p>
                  </div>
                </label>
              </div>
              <Field label="Damage Description">
                <textarea data-testid="damage-desc-input" value={form.damage_description}
                  onChange={(e) => set("damage_description", e.target.value)}
                  placeholder="Front-end collision, replaced bumper, hood and left fender…"
                  rows={2} className={inputCls} />
              </Field>
            </div>

            {/* Claim */}
            <div className="bg-[#0F0F11] rounded-xl border border-white/10 shadow-2xl p-5 sm:p-6 space-y-4">
              <SectionHead icon={ShieldCheck} num="4" title="Claim Info" note="Optional" />
              <Field label="Owner Name">
                <input data-testid="owner-input" value={form.owner_name}
                  onChange={(e) => set("owner_name", e.target.value)} className={inputCls} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Claim #">
                  <input data-testid="claim-input" value={form.claim_number}
                    onChange={(e) => set("claim_number", e.target.value)} className={inputCls + " font-mono-num"} />
                </Field>
                <Field label="Insurer">
                  <input data-testid="insurer-input" value={form.insurance_company}
                    onChange={(e) => set("insurance_company", e.target.value)} className={inputCls} />
                </Field>
              </div>
            </div>

            <button
              onClick={submit}
              disabled={loading}
              data-testid="calculate-btn"
              className="w-full h-12 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#C5A059] to-[#E5C17B] hover:brightness-110 text-black font-bold uppercase tracking-widest text-sm rounded-sm transition-all shadow-lg shadow-[#C5A059]/20 active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? (
                <><Spinner size={20} className="animate-spin" /> Appraising…</>
              ) : (
                <><Sparkle size={20} weight="fill" /> Run Appraisal</>
              )}
            </button>
          </div>

          {/* Right: report */}
          <div className="lg:col-span-6 xl:col-span-7">
            <ReportPreview appraisal={appraisal} loading={loading} onRefresh={setAppraisal} />
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10 bg-[#0F0F11] py-6 mt-6 text-center text-xs text-gray-500 print:hidden">
        <div className="max-w-7xl mx-auto px-4 space-y-1">
          <p className="font-display font-medium text-gray-300">
            CarValueRecovery — Automotive Property Damage Loss Appraisal System
          </p>
          <p className="text-[11px] text-gray-500">
            Estimates based on retail-market valuation, vehicle-history stigma modeling, and diminished-value standards across all 50 US states.
          </p>
        </div>
      </footer>
    </div>
  );
}
